import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, EyeOff, Lock, User, Mail, ArrowRight } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useNavigate, Link } from 'react-router-dom';
import GoogleLogin from './GoogleLogin';

const ease = [0.22, 1, 0.36, 1];
const NOISE = "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E\")";

const inputStyle = {
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.11)',
  borderRadius: 14,
  color: '#fff',
  width: '100%',
  padding: '12px 16px',
  fontSize: 14,
  outline: 'none',
  fontFamily: 'var(--font-body)',
  transition: 'border-color 0.2s, background 0.2s',
};

function InputField({ icon: Icon, rightIcon, onRightClick, type, name, value, onChange, placeholder, required, minLength }) {
  return (
    <div className="relative">
      {Icon && (
        <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
          <Icon size={15} color="rgba(255,255,255,0.32)" />
        </div>
      )}
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        minLength={minLength}
        style={{ ...inputStyle, paddingLeft: Icon ? 44 : 16, paddingRight: rightIcon ? 44 : 16 }}
        onFocus={e => { e.target.style.borderColor = 'rgba(255,255,255,0.28)'; e.target.style.background = 'rgba(255,255,255,0.08)'; }}
        onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.11)'; e.target.style.background = 'rgba(255,255,255,0.05)'; }}
      />
      {rightIcon && (
        <button
          type="button"
          onClick={onRightClick}
          className="absolute right-4 top-1/2 -translate-y-1/2"
          style={{ color: 'rgba(255,255,255,0.32)' }}
        >
          {rightIcon}
        </button>
      )}
    </div>
  );
}

export default function Login() {
  const [activeTab, setActiveTab] = useState('login');
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ name: '', email: '', password: '', confirmPassword: '' });
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const result = await login(loginData.email, loginData.password);
      if (result.success) navigate('/dashboard');
      else setError(result.error || 'Credenziali non valide');
    } catch (err) {
      setError(err.message || 'Errore di connessione');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (registerData.password !== registerData.confirmPassword) {
      setError('Le password non coincidono');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const result = await register(registerData.name, registerData.email, registerData.password);
      if (result.success) navigate('/dashboard');
      else setError(result.error || 'Errore durante la registrazione');
    } catch (err) {
      setError(err.message || 'Errore di connessione');
    } finally {
      setLoading(false);
    }
  };

  const switchTab = (tab) => { setActiveTab(tab); setError(''); };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
      style={{ background: '#000', fontFamily: 'var(--font-body)' }}
    >
      {/* gradient blobs */}
      <div className="absolute inset-0 pointer-events-none">
        <div style={{ position: 'absolute', width: '700px', height: '500px', right: '-150px', top: '-100px', background: 'radial-gradient(ellipse, rgba(35,136,255,0.16) 0%, transparent 65%)', filter: 'blur(100px)', transform: 'rotate(-18deg)' }} />
        <div style={{ position: 'absolute', width: '650px', height: '480px', left: '-150px', bottom: '-80px', background: 'radial-gradient(ellipse, rgba(74,58,255,0.13) 0%, transparent 65%)', filter: 'blur(100px)', transform: 'rotate(18deg)' }} />
        <div style={{ position: 'absolute', width: '400px', height: '400px', left: '50%', top: '50%', transform: 'translate(-50%,-50%)', background: 'radial-gradient(circle, rgba(35,136,255,0.07) 0%, transparent 70%)', filter: 'blur(80px)' }} />
        <div style={{ position: 'absolute', inset: 0, backgroundImage: NOISE, backgroundSize: '200px 200px', opacity: 0.45 }} />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.7, ease }}
        className="relative z-10 w-full max-w-md"
      >
        <div
          className="rounded-3xl p-8"
          style={{
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.12)',
            backdropFilter: 'blur(28px) saturate(140%)',
            WebkitBackdropFilter: 'blur(28px) saturate(140%)',
            boxShadow: '0 32px 80px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.10)',
          }}
        >
          {/* Header */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2.5 mb-6 group">
              <img src="/tente.ico" alt="" className="w-8 h-8 object-contain" />
              <span className="text-xl font-bold text-white" style={{ fontFamily: 'var(--font-heading)', letterSpacing: '-0.03em' }}>
                Gaked AI
              </span>
            </Link>
            <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'var(--font-heading)', letterSpacing: '-0.035em' }}>
              {activeTab === 'login' ? 'Bentornato.' : 'Crea il tuo account.'}
            </h1>
            <p className="text-sm" style={{ color: 'rgba(255,255,255,0.40)' }}>
              {activeTab === 'login' ? 'Accedi per iniziare a creare' : 'Unisciti e ottieni 1 credito gratis'}
            </p>
          </div>

          {/* Google OAuth */}
          <div className="mb-5">
            <GoogleLogin />
          </div>

          {/* Divider */}
          <div className="relative flex items-center gap-4 mb-5">
            <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.08)' }} />
            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.28)' }}>oppure</span>
            <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.08)' }} />
          </div>

          {/* Tabs */}
          <div
            className="flex mb-6 rounded-2xl p-1"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
          >
            {['login', 'register'].map(tab => (
              <button
                key={tab}
                onClick={() => switchTab(tab)}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200"
                style={activeTab === tab ? {
                  background: 'rgba(255,255,255,0.10)',
                  color: '#fff',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                } : { color: 'rgba(255,255,255,0.40)' }}
              >
                {tab === 'login' ? 'Accedi' : 'Registrati'}
              </button>
            ))}
          </div>

          {/* Error */}
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2 }}
                className="mb-5 px-4 py-3 rounded-2xl text-sm"
                style={{ background: 'rgba(255,60,60,0.10)', border: '1px solid rgba(255,60,60,0.20)', color: 'rgba(255,120,120,1)' }}
              >
                {error}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Forms */}
          <AnimatePresence mode="wait">
            {activeTab === 'login' ? (
              <motion.form
                key="login"
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 16 }}
                transition={{ duration: 0.25 }}
                onSubmit={handleLogin}
                className="flex flex-col gap-4"
              >
                <div>
                  <label className="block text-xs font-semibold mb-2 uppercase tracking-[0.12em]" style={{ color: 'rgba(255,255,255,0.36)' }}>Email</label>
                  <InputField
                    icon={Mail} type="email" name="email"
                    value={loginData.email}
                    onChange={e => setLoginData({ ...loginData, email: e.target.value })}
                    placeholder="La tua email" required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-2 uppercase tracking-[0.12em]" style={{ color: 'rgba(255,255,255,0.36)' }}>Password</label>
                  <InputField
                    icon={Lock}
                    type={showPwd ? 'text' : 'password'}
                    name="password"
                    value={loginData.password}
                    onChange={e => setLoginData({ ...loginData, password: e.target.value })}
                    placeholder="La tua password" required
                    rightIcon={showPwd ? <EyeOff size={15} /> : <Eye size={15} />}
                    onRightClick={() => setShowPwd(!showPwd)}
                  />
                </div>
                <motion.button
                  type="submit"
                  disabled={loading}
                  whileHover={{ opacity: 0.9, y: -1 }}
                  whileTap={{ scale: 0.98 }}
                  className="flex items-center justify-center gap-2 w-full py-4 rounded-2xl text-sm font-bold mt-2 disabled:opacity-50"
                  style={{ background: 'rgba(255,255,255,0.92)', color: '#000', boxShadow: '0 8px 24px rgba(0,0,0,0.3)', fontFamily: 'var(--font-body)' }}
                >
                  {loading ? (
                    <div className="w-4 h-4 border-2 border-black/20 border-t-black rounded-full animate-spin" />
                  ) : (
                    <>Accedi <ArrowRight size={15} /></>
                  )}
                </motion.button>
              </motion.form>
            ) : (
              <motion.form
                key="register"
                initial={{ opacity: 0, x: 16 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -16 }}
                transition={{ duration: 0.25 }}
                onSubmit={handleRegister}
                className="flex flex-col gap-4"
              >
                <div>
                  <label className="block text-xs font-semibold mb-2 uppercase tracking-[0.12em]" style={{ color: 'rgba(255,255,255,0.36)' }}>Nome</label>
                  <InputField
                    icon={User} type="text" name="name"
                    value={registerData.name}
                    onChange={e => setRegisterData({ ...registerData, name: e.target.value })}
                    placeholder="Il tuo nome" required minLength={2}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-2 uppercase tracking-[0.12em]" style={{ color: 'rgba(255,255,255,0.36)' }}>Email</label>
                  <InputField
                    icon={Mail} type="email" name="email"
                    value={registerData.email}
                    onChange={e => setRegisterData({ ...registerData, email: e.target.value })}
                    placeholder="La tua email" required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-2 uppercase tracking-[0.12em]" style={{ color: 'rgba(255,255,255,0.36)' }}>Password</label>
                  <InputField
                    icon={Lock}
                    type={showPwd ? 'text' : 'password'}
                    name="password"
                    value={registerData.password}
                    onChange={e => setRegisterData({ ...registerData, password: e.target.value })}
                    placeholder="Crea una password" required minLength={8}
                    rightIcon={showPwd ? <EyeOff size={15} /> : <Eye size={15} />}
                    onRightClick={() => setShowPwd(!showPwd)}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-2 uppercase tracking-[0.12em]" style={{ color: 'rgba(255,255,255,0.36)' }}>Conferma Password</label>
                  <InputField
                    icon={Lock}
                    type={showConfirmPwd ? 'text' : 'password'}
                    name="confirmPassword"
                    value={registerData.confirmPassword}
                    onChange={e => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
                    placeholder="Ripeti la password" required minLength={8}
                    rightIcon={showConfirmPwd ? <EyeOff size={15} /> : <Eye size={15} />}
                    onRightClick={() => setShowConfirmPwd(!showConfirmPwd)}
                  />
                </div>
                <motion.button
                  type="submit"
                  disabled={loading}
                  whileHover={{ opacity: 0.9, y: -1 }}
                  whileTap={{ scale: 0.98 }}
                  className="flex items-center justify-center gap-2 w-full py-4 rounded-2xl text-sm font-bold mt-2 disabled:opacity-50"
                  style={{ background: 'rgba(255,255,255,0.92)', color: '#000', boxShadow: '0 8px 24px rgba(0,0,0,0.3)', fontFamily: 'var(--font-body)' }}
                >
                  {loading ? (
                    <div className="w-4 h-4 border-2 border-black/20 border-t-black rounded-full animate-spin" />
                  ) : (
                    <>Crea account <ArrowRight size={15} /></>
                  )}
                </motion.button>

                <div
                  className="flex items-center justify-center gap-2 mt-1 py-2.5 px-4 rounded-2xl text-xs"
                  style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.46)' }}
                >
                  <span>🎁</span>
                  <span><strong style={{ color: 'rgba(255,255,255,0.72)' }}>1 credito gratis</strong> al momento della registrazione</span>
                </div>
              </motion.form>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
}
